import { useState, useEffect } from 'react';
import { Calendar, Loader2, Filter } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getMatches, getTeams, getGroups, getSettings, getThirdsStandings } from '../lib/api';
import { NotReadyYet } from '../components/common/NotReadyYet';
import MatchCard from '../components/common/MatchCard';
import { cn } from '../lib/utils';

const CalendarioPage = () => {
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [groups, setGroups] = useState([]);
  const [thirds, setThirds] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [matchesRes, teamsRes, groupsRes, settingsRes, thirdsRes] = await Promise.all([
          getMatches(),
          getTeams(),
          getGroups(),
          getSettings(),
          getThirdsStandings().catch(() => ({ data: [] }))
        ]);
        setMatches(matchesRes.data);
        setTeams(teamsRes.data);
        setGroups(groupsRes.data);
        setSettings(settingsRes.data);
        setThirds(thirdsRes.data || []);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getTeamById = (id) => teams.find(t => t.id === id);
  const getGroupById = (id) => groups.find(g => g.id === id);

  const getLabelForDate = (dateStr) => {
    const schedule = settings?.match_schedule || [];
    const entry = schedule.find(s => s.date === dateStr);
    return entry?.label || '';
  };

  const filteredMatches = matches
    .filter(m => {
      if (filter === 'played') return m.is_played;
      if (filter === 'upcoming') return !m.is_played;
      return true;
    })
    .filter(m => {
      if (groupFilter !== 'all') return m.group_id === groupFilter;
      return true;
    })
    .sort((a, b) => new Date(a.match_date) - new Date(b.match_date));

  // Group matches by date
  const matchesByDate = {};
  filteredMatches.forEach(match => {
    const dateKey = match.match_date?.substring(0, 10) || 'unknown';
    if (!matchesByDate[dateKey]) matchesByDate[dateKey] = [];
    matchesByDate[dateKey].push(match);
  });

  // Build synthetic placeholder matches for the knockout phase WHEN no real knockout match exists
  // (i match knockout reali vengono creati dopo la generazione automatica al termine dei gironi).
  // Vogliamo che il calendario pubblico mostri comunque date+orari delle eliminatorie,
  // e se la fase a gironi è conclusa, anche i NOMI delle squadre qualificate.
  const hasKnockoutReal = matches.some(m => ['quarterfinal', 'semifinal', 'final_3_4', 'final_1_2'].includes(m.phase));
  if (!hasKnockoutReal && filter !== 'played' && groupFilter === 'all') {
    // Risolvi i nomi reali dalla classifica live (utile quando il knockout non è stato ancora generato
    // ma le squadre qualificate sono già determinabili)
    const standingsByGroup = {};
    for (const g of groups) {
      standingsByGroup[g.name] = teams
        .filter(t => t.group_id === g.id)
        .sort((a, b) =>
          (b.points || 0) - (a.points || 0) ||
          (b.goal_difference || 0) - (a.goal_difference || 0) ||
          (b.goals_for || 0) - (a.goals_for || 0) ||
          (a.fair_play || 0) - (b.fair_play || 0)
        );
    }
    const bestThirds = thirds.filter(t => t.qualifies).slice(0, 2);
    const placeMap = {
      '1°A': standingsByGroup['A']?.[0],
      '2°A': standingsByGroup['A']?.[1],
      '1°B': standingsByGroup['B']?.[0],
      '2°B': standingsByGroup['B']?.[1],
      '1°C': standingsByGroup['C']?.[0],
      '2°C': standingsByGroup['C']?.[1],
      '1ª miglior 3ª': bestThirds[0] ? teams.find(t => t.id === bestThirds[0].id) : null,
      '2ª miglior 3ª': bestThirds[1] ? teams.find(t => t.id === bestThirds[1].id) : null,
    };
    const resolveName = (hint) => (placeMap[hint]?.name) || hint;

    const schedule = settings?.match_schedule || [];
    const findEntry = (re) => schedule.find(s => re.test((s.label || '').toLowerCase()));
    const timeAt = (e, idx) => (e && Array.isArray(e.times)) ? e.times[idx] : null;
    const placeholdersByEntry = (entry, slots) => {
      if (!entry?.date) return [];
      return slots.map((s, idx) => {
        const time = timeAt(entry, idx) || '20:30';
        const iso = `${entry.date}T${time}:00.000Z`;
        return {
          id: `placeholder-${entry.date}-${idx}-${s.label}`,
          match_date: iso,
          is_played: false,
          phase: s.phase,
          placeholder: true,
          placeholder_label: s.label,
          home_hint: resolveName(s.home),
          away_hint: resolveName(s.away),
        };
      });
    };
    const ko = [
      ...placeholdersByEntry(findEntry(/^1.*quarto.*andata/), [
        { phase: 'quarterfinal', label: 'QF1 · Andata', home: '1°C', away: '2°A' },
        { phase: 'quarterfinal', label: 'QF2 · Andata', home: '1°A', away: '1ª miglior 3ª' },
      ]),
      ...placeholdersByEntry(findEntry(/^1.*quarto.*ritorno/), [
        { phase: 'quarterfinal', label: 'QF1 · Ritorno', home: '2°A', away: '1°C' },
        { phase: 'quarterfinal', label: 'QF2 · Ritorno', home: '1ª miglior 3ª', away: '1°A' },
      ]),
      ...placeholdersByEntry(findEntry(/^2.*quarto.*andata/), [
        { phase: 'quarterfinal', label: 'QF3 · Andata', home: '1°B', away: '2ª miglior 3ª' },
        { phase: 'quarterfinal', label: 'QF4 · Andata', home: '2°B', away: '2°C' },
      ]),
      ...placeholdersByEntry(findEntry(/^2.*quarto.*ritorno/), [
        { phase: 'quarterfinal', label: 'QF3 · Ritorno', home: '2ª miglior 3ª', away: '1°B' },
        { phase: 'quarterfinal', label: 'QF4 · Ritorno', home: '2°C', away: '2°B' },
      ]),
      ...placeholdersByEntry(findEntry(/semifinal.*andata/), [
        { phase: 'semifinal', label: 'SF1 · Andata', home: 'Vincente QF1', away: 'Vincente QF2' },
        { phase: 'semifinal', label: 'SF2 · Andata', home: 'Vincente QF3', away: 'Vincente QF4' },
      ]),
      ...placeholdersByEntry(findEntry(/semifinal.*ritorno/), [
        { phase: 'semifinal', label: 'SF1 · Ritorno', home: 'Vincente QF2', away: 'Vincente QF1' },
        { phase: 'semifinal', label: 'SF2 · Ritorno', home: 'Vincente QF4', away: 'Vincente QF3' },
      ]),
      ...placeholdersByEntry(findEntry(/^finale$/), [
        { phase: 'final_3_4', label: 'Finale 3°/4° posto', home: 'Perdente SF1', away: 'Perdente SF2' },
        { phase: 'final_1_2', label: 'Finale 1°/2° posto', home: 'Vincente SF1', away: 'Vincente SF2' },
      ]),
    ];
    ko.forEach(p => {
      const dk = p.match_date.substring(0, 10);
      if (!matchesByDate[dk]) matchesByDate[dk] = [];
      matchesByDate[dk].push(p);
    });
  }

  const sortedDates = Object.keys(matchesByDate).sort();

  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00Z');
    return date.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!settings?.draw_completed) {
    return (
      <NotReadyYet
        title="Calendario non ancora disponibile"
        message="Il calendario delle partite sarà pubblicato dopo il sorteggio dei gironi. Torna presto!"
      />
    );
  }

  return (
    <div className="min-h-screen py-8 sm:py-12 px-4 max-w-5xl mx-auto" data-testid="calendario-page">
      <div className="text-center mb-8 sm:mb-12">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-bold text-primary border border-primary/30 rounded-full mb-4">
          <Calendar className="w-4 h-4" />
          CALENDARIO
        </span>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black mb-3">
          CALENDARIO & <span className="text-primary">RISULTATI</span>
        </h1>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
        <Filter className="w-4 h-4 text-slate-500" />
        <span className="text-sm text-slate-400">Filtri:</span>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[160px] bg-slate-900 border-slate-800 text-sm" data-testid="match-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le partite</SelectItem>
            <SelectItem value="upcoming">Da giocare</SelectItem>
            <SelectItem value="played">Giocate</SelectItem>
          </SelectContent>
        </Select>
        <Select value={groupFilter} onValueChange={setGroupFilter}>
          <SelectTrigger className="w-[120px] bg-slate-900 border-slate-800 text-sm" data-testid="group-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            {groups.map(g => (
              <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Matches grouped by date */}
      {sortedDates.length > 0 ? (
        <div className="space-y-8">
          {sortedDates.map(dateKey => {
            const dayMatches = matchesByDate[dateKey].sort(
              (a, b) => new Date(a.match_date) - new Date(b.match_date)
            );
            const label = getLabelForDate(dateKey);
            const isRecupero = label?.toLowerCase().trim() === 'recupero';

            return (
              <div key={dateKey} data-testid={`cal-day-${dateKey}`}>
                {/* Date Header */}
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="font-heading text-base sm:text-lg font-bold text-primary uppercase">
                    {formatDateHeader(dateKey)}
                  </h2>
                  {label && (
                    <span className={cn(
                      "text-xs px-2 py-0.5 rounded-full font-semibold",
                      isRecupero
                        ? "bg-yellow-500/10 text-yellow-400"
                        : "bg-primary/10 text-primary"
                    )}>
                      {label}
                    </span>
                  )}
                  <span className="text-xs text-slate-500">
                    {dayMatches.length} {dayMatches.length === 1 ? 'partita' : 'partite'}
                  </span>
                </div>

                {/* Match Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {dayMatches.map(match => (
                    match.placeholder ? (
                      <div
                        key={match.id}
                        className="match-card border-l-dashed border-l-2 border-l-slate-700 opacity-90"
                        data-testid={`match-card-placeholder-${match.id}`}
                      >
                        <div className="flex items-center justify-between w-full gap-2 sm:gap-4">
                          <span className="text-xs font-numbers text-slate-400 w-10 shrink-0">
                            {new Date(match.match_date).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}
                          </span>
                          <div className="flex-1 text-right min-w-0">
                            <p className="font-heading text-sm sm:text-base font-bold uppercase leading-tight text-slate-300 truncate">
                              {match.home_hint}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 px-2 flex-shrink-0">
                            <span className="text-slate-500 font-heading text-base">VS</span>
                          </div>
                          <div className="flex-1 text-left min-w-0">
                            <p className="font-heading text-sm sm:text-base font-bold uppercase leading-tight text-slate-300 truncate">
                              {match.away_hint}
                            </p>
                          </div>
                        </div>
                        <div className="mt-2">
                          <span className="text-[10px] sm:text-xs px-2 py-1 bg-gold/20 text-gold rounded font-semibold uppercase">
                            {match.placeholder_label}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <MatchCard
                        key={match.id}
                        match={match}
                        homeTeam={getTeamById(match.home_team_id)}
                        awayTeam={getTeamById(match.away_team_id)}
                        showDate={false}
                        showTime={true}
                      />
                    )
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-8 text-center">
            <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">Nessuna partita in calendario</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CalendarioPage;
